import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Inbox,
  Mail,
  Camera,
  Mic,
  CheckCircle2,
  ArrowRight,
  Calendar,
  Bell,
  Repeat,
  Target,
  Users,
  Sparkles,
  Smartphone,
  Share,
  ArrowDown,
  Download,
  PlayCircle,
  ChevronDown,
  ChevronUp,
  Star,
} from "lucide-react";
import { Helmet } from "react-helmet";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { AnimatedSection, StaggerContainer } from "@/components/marketing/AnimatedSection";
import { getAuthUrl } from "@/lib/authUrl";
import { useState } from "react";

const captureModes = [
  {
    icon: Mail,
    title: "Forward an email",
    description: "It becomes a task automatically.",
  },
  {
    icon: Camera,
    title: "Snap a photo",
    description: "Receipts, flyers, schedules — captured instantly.",
  },
  {
    icon: Mic,
    title: "Say it out loud",
    description: "Voice in, organized task out.",
  },
];

const coreValues = [
  {
    icon: Inbox,
    title: "Capture anything in seconds",
    points: [
      "Forward an email → becomes a task",
      "Snap a photo → becomes a task",
      "Say it out loud → becomes a task",
    ],
    footer: "You don't organize tasks. You capture them as they happen.",
  },
  {
    icon: Target,
    title: "Always know what matters next",
    points: [
      "Tasks organized by life categories",
      "Due dates, priorities, reminders",
      "Clear daily focus",
    ],
    footer: "No more scanning multiple apps trying to figure out what's urgent.",
  },
  {
    icon: Users,
    title: "Keep your household in sync",
    points: [
      "Share tasks with your partner",
      "Assign responsibilities clearly",
      "Everyone sees the same plan",
    ],
    footer: "No more \"I thought you had it.\"",
  },
  {
    icon: Repeat,
    title: "Turn chaos into routines",
    points: [
      "Groceries",
      "School prep",
      "Weekly admin",
    ],
    footer: "Stop reinventing the same week over and over.",
  },
];

const useCaseSteps = [
  { icon: Mail, text: "Forward the school email → it's now a task" },
  { icon: Mic, text: "Say \"buy groceries\" → added instantly" },
  { icon: Users, text: "Assign practice prep to your partner" },
  { icon: Calendar, text: "Bill due Monday — reminder set" },
];

const pricingPlans = [
  {
    name: "Free",
    tagline: "For getting started",
    price: "$0",
    period: "forever",
    features: ["Capture tasks", "Basic organization"],
    cta: "Start Free",
    popular: false,
  },
  {
    name: "Pro",
    tagline: "For running your life without friction",
    price: "$9.99",
    period: "/month",
    features: [
      "Unlimited tasks",
      "Email, voice, and image capture",
      "Categories, priorities, routines",
      "Smart reminders",
    ],
    cta: "Get Pro",
    popular: true,
    badge: "Best for individuals",
  },
  {
    name: "Family",
    tagline: "For running your household together",
    price: "$14.99",
    period: "/month",
    features: [
      "Everything in Pro",
      "Share and assign tasks",
      "Keep everyone in sync",
    ],
    cta: "Get Family",
    popular: false,
    badge: "Covers your entire household",
  },
];

const testimonials = [
  { quote: "I stopped forgetting things within days." },
  { quote: "We actually coordinate now." },
  { quote: "This replaced multiple apps for us." },
];

const faqs = [
  {
    question: "Is Serento just a to-do list?",
    answer:
      "No. Serento is a complete life and household organization system. It captures tasks from emails, photos, and voice, organizes them by life category, and keeps your whole household in sync.",
  },
  {
    question: "How long does setup take?",
    answer:
      "Most families are up and running in under 5 minutes. Start capturing immediately — Serento learns your patterns over time.",
  },
  {
    question: "Can my partner and I share tasks?",
    answer:
      "Yes. The Family plan lets you share tasks, assign responsibilities, and keep everyone on the same page. No more \"I thought you had it.\"",
  },
  {
    question: "Is there a free plan?",
    answer:
      "Yes. The Free plan lets you capture tasks and use basic organization. Upgrade to Pro or Family when you need unlimited capture, smart reminders, and household sharing.",
  },
  {
    question: "What devices are supported?",
    answer:
      "Serento installs like an app on any phone, tablet, or computer — no App Store, no updates. Just open it in your browser and add it to your home screen.",
  },
];

export default function MarketingHome() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <Helmet>
        <title>Serento — Run your entire household without relying on memory</title>
        <meta
          name="description"
          content="Capture anything instantly — emails, photos, voice — and keep your family perfectly organized in one place. Built for busy households."
        />
        <meta name="robots" content="index, follow" />
        <meta
          name="keywords"
          content="household organizer, family task manager, life admin app, shared family tasks, organize household, capture tasks from email"
        />
        <link rel="canonical" href="https://serento.ai/" />
        <meta property="og:title" content="Serento — Run your entire household without relying on memory" />
        <meta
          property="og:description"
          content="Capture anything instantly — emails, photos, voice — and keep your family perfectly organized in one place."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://serento.ai/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Serento",
            url: "https://serento.ai",
            logo: "https://serento.ai/serento-logo.png",
            description:
              "Run your entire household without relying on memory. Capture tasks from emails, photos, and voice.",
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: { "@type": "Answer", text: faq.answer },
            })),
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <MarketingHeader />

        <main>
          {/* HERO */}
          <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <AnimatedSection animation="fade-up">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-foreground">
                  Run your entire household without relying on memory
                </h1>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={100}>
                <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Capture anything instantly — emails, photos, voice — and keep your family perfectly organized in one place.
                </p>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={200}>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                  <Button size="lg" className="h-12 px-8 hover-scale" asChild>
                    <a href={getAuthUrl()}>
                      Get Started Free
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-12 px-8 hover-scale"
                    onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
                  >
                    <PlayCircle className="mr-2 w-4 h-4" />
                    Watch 60-sec Demo
                  </Button>
                </div>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={300}>
                <p className="text-sm text-muted-foreground">
                  Built for busy families managing real life, not perfect systems.
                </p>
              </AnimatedSection>
            </div>
          </section>

          {/* PROBLEM */}
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
            <div className="max-w-3xl mx-auto text-center">
              <AnimatedSection animation="fade-up">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-foreground">
                  The problem isn't organization. It's overload.
                </h2>
              </AnimatedSection>
              <AnimatedSection animation="fade-up" delay={100}>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>School emails. Grocery lists. Bills. Activities.</p>
                  <p>Things don't fall apart because you're disorganized.</p>
                  <p className="text-foreground font-medium">
                    They fall apart because everything lives in different places.
                  </p>
                  <p>Your inbox. Notes. Texts. Your head.</p>
                  <p>And eventually, something gets missed.</p>
                </div>
              </AnimatedSection>
            </div>
          </section>

          {/* SOLUTION */}
          <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <AnimatedSection animation="fade-up">
                <div className="text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                    One system for everything life throws at you
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                    Serento captures, organizes, and tracks everything in one place — so nothing slips through. No switching apps. No mental juggling.
                  </p>
                </div>
              </AnimatedSection>

              <div className="grid md:grid-cols-3 gap-6 mt-12">
                {captureModes.map((mode, idx) => (
                  <AnimatedSection key={idx} animation="fade-up" delay={idx * 100}>
                    <Card className="p-6 h-full hover:shadow-lg transition-shadow text-center">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                        <mode.icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2 text-foreground">{mode.title}</h3>
                      <p className="text-muted-foreground text-sm">{mode.description}</p>
                    </Card>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </section>

          {/* CORE VALUE */}
          <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
            <div className="max-w-6xl mx-auto">
              <AnimatedSection animation="fade-up">
                <div className="text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                    Built for the way real life actually happens
                  </h2>
                </div>
              </AnimatedSection>

              <div className="grid md:grid-cols-2 gap-6">
                {coreValues.map((value, idx) => (
                  <AnimatedSection key={idx} animation="fade-up" delay={idx * 100}>
                    <Card className="p-8 h-full hover:shadow-lg transition-shadow">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                        <value.icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold mb-4 text-foreground">{value.title}</h3>
                      <ul className="space-y-2 mb-4">
                        {value.points.map((point, i) => (
                          <li key={i} className="flex items-start gap-2 text-muted-foreground">
                            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm text-foreground italic border-t border-border pt-4">
                        {value.footer}
                      </p>
                    </Card>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </section>

          {/* USE CASE */}
          <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <AnimatedSection animation="fade-up">
                <div className="text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                    A normal day, without the chaos
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                    A school email comes in. You need groceries. Your kid has practice. A bill is due Monday.
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={100}>
                <Card className="p-8">
                  <p className="text-sm font-semibold text-primary mb-6 uppercase tracking-wide">With Serento</p>
                  <div className="space-y-4">
                    {useCaseSteps.map((step, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <step.icon className="w-5 h-5 text-primary" />
                        </div>
                        <span className="text-foreground">{step.text}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-8 text-center text-lg font-medium text-foreground">
                    Nothing forgotten. Nothing scattered.
                  </p>
                </Card>
              </AnimatedSection>
            </div>
          </section>

          {/* DIFFERENTIATION */}
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
            <div className="max-w-4xl mx-auto">
              <AnimatedSection animation="fade-up">
                <div className="text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                    Why this works when other apps don't
                  </h2>
                </div>
              </AnimatedSection>

              <div className="grid md:grid-cols-2 gap-6">
                <AnimatedSection animation="fade-right">
                  <Card className="p-8 h-full bg-muted/50">
                    <h3 className="text-lg font-semibold mb-4 text-muted-foreground">Most tools</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li>• Built for work, not real life</li>
                      <li>• Require manual organization</li>
                      <li>• Don't handle shared responsibilities</li>
                    </ul>
                  </Card>
                </AnimatedSection>
                <AnimatedSection animation="fade-left">
                  <Card className="p-8 h-full border-primary/30 bg-card">
                    <h3 className="text-lg font-semibold mb-4 text-primary">Serento</h3>
                    <ul className="space-y-3 text-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        Captures tasks automatically
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        Organizes your entire life
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        Works across your household
                      </li>
                    </ul>
                  </Card>
                </AnimatedSection>
              </div>

              <AnimatedSection animation="fade-up" delay={200}>
                <p className="text-center text-lg font-medium text-foreground mt-8">
                  This isn't another to-do list. It's a system.
                </p>
              </AnimatedSection>
            </div>
          </section>

          {/* PRICING */}
          <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <AnimatedSection animation="fade-up">
                <div className="text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                    Simple pricing that scales with your life
                  </h2>
                </div>
              </AnimatedSection>

              <div className="grid md:grid-cols-3 gap-6">
                {pricingPlans.map((plan, idx) => (
                  <AnimatedSection key={idx} animation="fade-up" delay={idx * 100}>
                    <Card
                      className={`p-8 h-full flex flex-col relative ${
                        plan.popular ? "border-primary shadow-lg md:scale-105" : ""
                      }`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                          Most Popular
                        </div>
                      )}
                      <h3 className="text-2xl font-bold text-foreground mb-1">{plan.name}</h3>
                      <p className="text-sm text-muted-foreground mb-4">{plan.tagline}</p>
                      <div className="mb-6">
                        <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                        <span className="text-muted-foreground">{plan.period}</span>
                      </div>
                      <ul className="space-y-3 mb-6 flex-1">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-foreground">
                            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {plan.badge && (
                        <p className="text-xs text-muted-foreground italic mb-4">👉 {plan.badge}</p>
                      )}
                      <Button
                        className="w-full"
                        variant={plan.popular ? "default" : "outline"}
                        asChild
                      >
                        <a href={getAuthUrl()}>{plan.cta}</a>
                      </Button>
                    </Card>
                  </AnimatedSection>
                ))}
              </div>

              <AnimatedSection animation="fade-up" delay={300}>
                <p className="text-center text-muted-foreground mt-8">
                  Start free. Upgrade when you need more control.
                </p>
              </AnimatedSection>
            </div>
          </section>

          {/* SOCIAL PROOF */}
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
            <div className="max-w-5xl mx-auto">
              <AnimatedSection animation="fade-up">
                <div className="text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                    Finally, everything in one place
                  </h2>
                </div>
              </AnimatedSection>

              <StaggerContainer className="grid md:grid-cols-3 gap-6" staggerDelay={100}>
                {testimonials.map((t, idx) => (
                  <Card key={idx} className="p-6 h-full">
                    <div className="flex gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                      ))}
                    </div>
                    <p className="text-foreground italic">"{t.quote}"</p>
                  </Card>
                ))}
              </StaggerContainer>
            </div>
          </section>

          {/* MOBILE APP DOWNLOAD */}
          <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <AnimatedSection animation="fade-up">
                <div className="text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                    Download the Mobile App
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                    Serento installs like an app — no App Store, no updates, works everywhere. Get it on your phone in seconds.
                  </p>
                </div>
              </AnimatedSection>

              <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                <AnimatedSection animation="fade-up" delay={100}>
                  <Card className="p-6 h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Smartphone className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">iPhone / iPad</h3>
                    </div>
                    <div className="space-y-4">
                      <div className="relative bg-muted/50 rounded-xl p-4 text-center">
                        <div className="flex items-center justify-center gap-2 text-sm font-medium flex-wrap">
                          <span>Tap</span>
                          <div className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 rounded text-primary">
                            <Share className="w-4 h-4" />
                            <span>Share</span>
                          </div>
                          <span>→</span>
                          <span className="text-primary font-semibold">Add to Home Screen</span>
                        </div>
                        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 animate-bounce">
                          <ArrowDown className="w-4 h-4 text-primary" />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground text-center">
                        Look for the Share button at the bottom of Safari
                      </p>
                    </div>
                  </Card>
                </AnimatedSection>

                <AnimatedSection animation="fade-up" delay={200}>
                  <Card className="p-6 h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Smartphone className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Android</h3>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-muted/50 rounded-xl p-4 text-center">
                        <p className="text-sm font-medium">
                          Tap <span className="text-primary font-semibold">Install app</span> when the prompt appears
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground text-center">
                        Or tap Chrome's menu (⋮) → "Add to Home screen"
                      </p>
                    </div>
                  </Card>
                </AnimatedSection>
              </div>

              <AnimatedSection animation="fade-up" delay={300}>
                <div className="mt-8 text-center">
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-12 px-8"
                    onClick={() => navigate("/install")}
                  >
                    <Download className="w-5 h-5 mr-2" />
                    View Full Installation Guide
                  </Button>
                </div>
              </AnimatedSection>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
            <div className="max-w-3xl mx-auto">
              <AnimatedSection animation="fade-up">
                <div className="text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                    Frequently Asked Questions
                  </h2>
                </div>
              </AnimatedSection>

              <div className="space-y-3">
                {faqs.map((faq, idx) => (
                  <AnimatedSection key={idx} animation="fade-up" delay={idx * 50}>
                    <div className="border border-border rounded-lg overflow-hidden">
                      <button
                        onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                        className="w-full flex items-center justify-between p-4 text-left bg-card hover:bg-muted/50 transition-colors"
                      >
                        <span className="font-medium text-foreground pr-4">{faq.question}</span>
                        {openFaq === idx ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        )}
                      </button>
                      {openFaq === idx && (
                        <div className="p-4 pt-0 bg-card">
                          <p className="text-muted-foreground">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto text-center">
              <AnimatedSection animation="fade-up">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                  Stop keeping your life in your head
                </h2>
              </AnimatedSection>
              <AnimatedSection animation="fade-up" delay={100}>
                <p className="text-lg text-muted-foreground mb-8">
                  Capture everything. Stay organized. Run your household with clarity.
                </p>
              </AnimatedSection>
              <AnimatedSection animation="fade-up" delay={200}>
                <Button size="lg" className="h-12 px-8 hover-scale" asChild>
                  <a href={getAuthUrl()}>
                    Get Started Free
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </AnimatedSection>
            </div>
          </section>
        </main>

        <MarketingFooter />
      </div>
    </>
  );
}
